import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Popup } from "semantic-ui-react";
import { UpdateCobranzaCollectionManagement } from "src/graphql/agenda";
import { useCompanyEquipoMembers } from "src/hooks/useCompanyEquipoMembers";
import {
  avatarColorFromString,
  getChannelOptions,
  getDecisionOptions,
  getInitials,
  goToConfiguracionEquipo,
} from "./cobranzaGestionUtils";
import CobranzaGestionStateIcon from "./CobranzaGestionStateIcon";

const gestionDropdownCloseHandlers = new Set();

function closeOtherGestionDropdowns(exceptHandler) {
  gestionDropdownCloseHandlers.forEach((handler) => {
    if (handler !== exceptHandler) handler();
  });
}

function getAssigneeFromRow(row) {
  const agenda = row?.agenda;
  if (!agenda) return null;
  if (agenda.collectionAssignee?.userId) return agenda.collectionAssignee;
  if (agenda.collectionAssigneeUserId) {
    return {
      userId: agenda.collectionAssigneeUserId,
      name: agenda.collectionAssigneeName,
      email: agenda.collectionAssigneeEmail,
    };
  }
  return null;
}

function ProcessBadge({
  label,
  value,
  options,
  disabled,
  onSelect,
  onOpen,
  onClose,
  readOnly = false,
}) {
  const current = options.find((o) => o.value === value);
  const display = current?.label || label;
  const className = `cf-gestion-process-badge cf-gestion-process-badge--neutral${
    readOnly ? " cf-gestion-process-badge--readonly" : ""
  }`;
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const menuOpenRef = useRef(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState(null);
  const isInteractive = !readOnly;

  const stopOnly = (e) => e.stopPropagation();
  const blockPointer = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const closeMenu = () => {
    if (!menuOpenRef.current) return;
    menuOpenRef.current = false;
    setMenuOpen(false);
    onClose?.();
  };

  const openMenu = () => {
    if (readOnly || disabled || menuOpenRef.current) return;
    closeOtherGestionDropdowns(closeMenu);
    menuOpenRef.current = true;
    setMenuOpen(true);
    onOpen?.();
  };

  useEffect(() => {
    gestionDropdownCloseHandlers.add(closeMenu);
    return () => {
      gestionDropdownCloseHandlers.delete(closeMenu);
      if (menuOpenRef.current) {
        menuOpenRef.current = false;
        onClose?.();
      }
    };
  }, []);

  const updateMenuPosition = () => {
    const triggerNode = triggerRef.current;
    if (!triggerNode || typeof window === "undefined") return;

    const rect = triggerNode.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const menuWidth = Math.max(rect.width, Math.min(168, viewportWidth - 24));
    const left = Math.max(12, Math.min(rect.left, viewportWidth - menuWidth - 12));
    const spaceBelow = viewportHeight - rect.bottom - 12;
    const spaceAbove = rect.top - 12;
    const openUpwards = spaceBelow < 180 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(120, Math.min(260, openUpwards ? spaceAbove : spaceBelow));

    setMenuStyle({
      left,
      right: "auto",
      width: menuWidth,
      maxHeight,
      top: openUpwards ? "auto" : rect.bottom + 8,
      bottom: openUpwards ? viewportHeight - rect.top + 8 : "auto",
    });
  };

  useLayoutEffect(() => {
    if (!isInteractive || !menuOpen) return;
    updateMenuPosition();
  }, [isInteractive, menuOpen, options.length]);

  useEffect(() => {
    if (!isInteractive || !menuOpen) return undefined;

    const handleViewportChange = () => updateMenuPosition();
    const handlePointerDown = (event) => {
      if (
        menuRef.current?.contains(event.target) ||
        triggerRef.current?.contains(event.target)
      ) {
        return;
      }
      closeMenu();
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };

    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    window.addEventListener("mousedown", handlePointerDown, true);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
      window.removeEventListener("mousedown", handlePointerDown, true);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isInteractive, menuOpen]);

  useEffect(() => {
    if (!isInteractive) return;
    if (disabled) {
      closeMenu();
    }
  }, [isInteractive, disabled]);

  const menuPortal =
    menuOpen && menuStyle && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={menuRef}
            className="cf-gestion-process-menu cf-gestion-process-menu--portal"
            style={menuStyle}
            role="menu"
            aria-label={label}
            onClick={stopOnly}
            onMouseDown={blockPointer}
          >
            {options.map((opt) => (
              <button
                type="button"
                key={opt.value ?? "__clear__"}
                role="menuitemradio"
                aria-checked={value === opt.value}
                className={`cf-gestion-process-menu__item${
                  value === opt.value ? " is-selected" : ""
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  closeMenu();
                  onSelect(opt.value);
                }}
              >
                <span className="cf-gestion-process-menu__item-label">{opt.label}</span>
                <CobranzaGestionStateIcon
                  className={opt.className || "cf-gestion-badge--neutral"}
                  icon={opt.icon || "minus"}
                />
              </button>
            ))}
          </div>,
          document.body
        )
      : null;

  if (!isInteractive) {
    return (
      <span className={className} aria-label={label} title={display}>
        <span className="cf-gestion-process-badge__label">{display}</span>
        <CobranzaGestionStateIcon
          className={current?.className || "cf-gestion-badge--neutral"}
          icon={current?.icon || "minus"}
        />
      </span>
    );
  }

  return (
    <>
      <span className="cf-gestion-process-badge-wrap">
        <button
          ref={triggerRef}
          type="button"
          className={className}
          disabled={disabled}
          aria-label={label}
          title={display}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={(e) => {
            e.stopPropagation();
            if (menuOpenRef.current) {
              closeMenu();
            } else {
              openMenu();
            }
          }}
          onMouseDown={blockPointer}
        >
          <span className="cf-gestion-process-badge__label">{display}</span>
          <CobranzaGestionStateIcon
            className={current?.className || "cf-gestion-badge--neutral"}
            icon={current?.icon || "minus"}
          />
        </button>
      </span>
      {menuPortal}
    </>
  );
}

export default function CobranzaGestionMetaBar({
  row,
  companyId,
  documentId = null,
  openDocumentIds = [],
  showAssignee = true,
  assigneeOnly = false,
  showChannel = true,
  compact = false,
  processReadOnly = false,
  onUpdated,
  onMenuOpenChange,
  onManagementChange,
}) {
  const { t } = useTranslation("cobranza");
  const clearOption = useMemo(
    () => ({ value: null, label: t("gestionDecision.clear"), className: "cf-gestion-badge--neutral" }),
    [t]
  );
  const decisionOptions = useMemo(() => [clearOption, ...getDecisionOptions(t)], [t, clearOption]);
  const channelOptions = useMemo(() => [clearOption, ...getChannelOptions(t)], [t, clearOption]);
  const agendaId = row?.agenda?.id;
  const disabled = !agendaId;
  const assignee = getAssigneeFromRow(row);
  const [saving, setSaving] = useState(false);
  const openCountRef = useRef(0);
  const assigneeTriggerRef = useRef(null);
  const assigneeMenuRef = useRef(null);
  const assigneeMenuOpenRef = useRef(false);
  const [assigneeMenuOpen, setAssigneeMenuOpen] = useState(false);
  const [assigneeMenuStyle, setAssigneeMenuStyle] = useState(null);
  const history = useHistory();

  const { teamsWithMembers, totalAssigned, fetching: teamsFetching } =
    useCompanyEquipoMembers(companyId);

  const cobranzaTeamsWithMembers = useMemo(
    () =>
      (teamsWithMembers || []).filter(
        (team) =>
          String(team?.key || "").toLowerCase() === "collections" ||
          String(team?.name || "").toLowerCase() === "cobranza"
      ),
    [teamsWithMembers]
  );

  const cobranzaAssignedCount = useMemo(
    () =>
      cobranzaTeamsWithMembers.reduce(
        (total, team) => total + (Array.isArray(team?.members) ? team.members.length : 0),
        0
      ),
    [cobranzaTeamsWithMembers]
  );

  const bumpMenuOpen = (delta) => {
    openCountRef.current = Math.max(0, openCountRef.current + delta);
    onMenuOpenChange?.(openCountRef.current > 0);
  };

  const handleMenuOpen = () => bumpMenuOpen(1);
  const handleMenuClose = () => bumpMenuOpen(-1);

  const closeAssigneeMenu = () => {
    if (!assigneeMenuOpenRef.current) return;
    assigneeMenuOpenRef.current = false;
    setAssigneeMenuOpen(false);
    handleMenuClose();
  };

  const openAssigneeMenu = () => {
    if (assigneeMenuOpenRef.current || disabled || saving) return;
    closeOtherGestionDropdowns(closeAssigneeMenu);
    assigneeMenuOpenRef.current = true;
    setAssigneeMenuOpen(true);
    handleMenuOpen();
  };

  useEffect(() => {
    gestionDropdownCloseHandlers.add(closeAssigneeMenu);
    return () => {
      gestionDropdownCloseHandlers.delete(closeAssigneeMenu);
      if (assigneeMenuOpenRef.current) {
        assigneeMenuOpenRef.current = false;
        handleMenuClose();
      }
    };
  }, []);

  const updateManagement = UpdateCobranzaCollectionManagement();

  const handleManagementChange = (nextInput) => {
    onManagementChange?.({
      ...nextInput,
      documentId: documentId ? String(documentId) : null,
      openDocumentIds: documentId ? [] : (openDocumentIds || []).map(String),
    });
  };

  const persist = async (input) => {
    if (!agendaId || !companyId) return;
    const payload = { ...input };
    if (documentId) {
      payload.documentId = String(documentId);
    }
    if (openDocumentIds.length > 0) {
      payload.openDocumentIds = openDocumentIds.map(String);
      if (
        !documentId &&
        (input.collectionDecision !== undefined || input.collectionChannel !== undefined)
      ) {
        payload.applyToOpenDocuments = true;
      }
    }
    setSaving(true);
    try {
      const result = await updateManagement.executeMutation({
        agendaId,
        companyId,
        input: payload,
      });
      if (result.error) throw result.error;
      onUpdated?.({ requestPolicy: "network-only" });
    } catch (err) {
      console.error("[CobranzaGestionMetaBar]", err);
    } finally {
      setSaving(false);
    }
  };

  const openEquipoConfig = (e) => {
    e.stopPropagation();
    closeAssigneeMenu();
    goToConfiguracionEquipo(history, { newTab: true });
  };

  const updateAssigneeMenuPosition = () => {
    const triggerNode = assigneeTriggerRef.current;
    if (!triggerNode || typeof window === "undefined") return;

    const rect = triggerNode.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const menuWidth = Math.max(220, Math.min(420, viewportWidth - 24));
    const left = Math.max(12, Math.min(rect.right - menuWidth, viewportWidth - menuWidth - 12));
    const spaceBelow = viewportHeight - rect.bottom - 12;
    const spaceAbove = rect.top - 12;
    const openUpwards = spaceBelow < 280 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(160, Math.min(420, openUpwards ? spaceAbove : spaceBelow));

    setAssigneeMenuStyle({
      left,
      width: menuWidth,
      maxHeight,
      top: openUpwards ? "auto" : rect.bottom + 8,
      bottom: openUpwards ? viewportHeight - rect.top + 8 : "auto",
    });
  };

  useLayoutEffect(() => {
    if (!assigneeMenuOpen) return;
    updateAssigneeMenuPosition();
  }, [assigneeMenuOpen, cobranzaTeamsWithMembers, cobranzaAssignedCount]);

  useEffect(() => {
    if (!assigneeMenuOpen) return undefined;

    const handleViewportChange = () => updateAssigneeMenuPosition();
    const handlePointerDown = (event) => {
      if (
        assigneeMenuRef.current?.contains(event.target) ||
        assigneeTriggerRef.current?.contains(event.target)
      ) {
        return;
      }
      closeAssigneeMenu();
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeAssigneeMenu();
      }
    };

    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);
    window.addEventListener("mousedown", handlePointerDown, true);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
      window.removeEventListener("mousedown", handlePointerDown, true);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [assigneeMenuOpen]);

  useEffect(() => {
    if (disabled || saving) {
      closeAssigneeMenu();
    }
  }, [disabled, saving]);

  useEffect(() => {
    return () => {
      if (openCountRef.current > 0) {
        openCountRef.current = 0;
        onMenuOpenChange?.(false);
      }
    };
  }, [onMenuOpenChange]);

  const assigneeTrigger = (
    <button
      ref={assigneeTriggerRef}
      type="button"
      className="cf-gestion-avatar-btn"
      disabled={disabled || saving}
      aria-label={t("gestionMetaBar.assigneeAria")}
      title={
        assignee?.name || assignee?.email
          ? t("gestionMetaBar.assigneeTitle", {
              name: assignee.name || assignee.email,
            })
          : cobranzaAssignedCount > 0
            ? t("gestionMetaBar.chooseAssignee")
            : t("gestionMetaBar.addTeamMembers")
      }
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (assigneeMenuOpen) {
          closeAssigneeMenu();
        } else {
          openAssigneeMenu();
        }
      }}
      onMouseDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      style={{
        backgroundColor: avatarColorFromString(
          assignee?.userId || assignee?.email || "unassigned"
        ),
      }}
    >
      <span className="cf-gestion-avatar-initials">
        {assignee ? (
          getInitials(assignee.name, assignee.email)
        ) : (
          <span className="cf-gestion-avatar-icon">?</span>
        )}
      </span>
    </button>
  );

  const stopOnly = (e) => e.stopPropagation();
  const blockPointer = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const assigneeMenuPortal =
    assigneeMenuOpen && assigneeMenuStyle && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={assigneeMenuRef}
            className="cf-gestion-equipo-menu cf-gestion-equipo-menu--portal"
            style={assigneeMenuStyle}
            onClick={stopOnly}
            onMouseDown={blockPointer}
          >
            <div className="cf-gestion-equipo-menu__title">{t("gestionMetaBar.menuTitle")}</div>
            <button
              type="button"
              className="cf-gestion-equipo-menu__action cf-gestion-equipo-menu__goto"
              onClick={openEquipoConfig}
            >
              <span className="cf-gestion-equipo-menu__goto-path">
                <span className="cf-gestion-equipo-menu__goto-section">
                  {t("gestionMetaBar.configTeam")}
                </span>
                <span className="cf-gestion-equipo-menu__goto-arrow" aria-hidden>
                  →
                </span>
                <span className="cf-gestion-equipo-menu__goto-section">
                  {t("gestionMetaBar.team")}
                </span>
              </span>
              <span className="cf-gestion-equipo-menu__goto-cta">
                {t("gestionMetaBar.addTeamMembers")}
              </span>
            </button>

            {teamsFetching && cobranzaAssignedCount === 0 && (
              <div className="cf-gestion-equipo-menu__hint">
                {t("gestionMetaBar.loadingMembers")}
              </div>
            )}
            {!teamsFetching && cobranzaAssignedCount === 0 && (
              <div className="cf-gestion-equipo-menu__hint">
                {t("gestionMetaBar.noMembers")}
              </div>
            )}

            {cobranzaTeamsWithMembers.map((team) => (
              <React.Fragment key={team.key}>
                <div className="cf-gestion-equipo-menu__team">{team.name}</div>
                {team.members.map((m) => {
                  const rawName = (m.name || "").trim();
                  const displayName =
                    rawName && rawName !== "-" ? rawName : m.email || "Usuario";
                  const showEmail = Boolean(m.name && m.email);
                  const isSelected = String(assignee?.userId) === String(m.userId);

                  return (
                    <button
                      type="button"
                      key={`${team.key}-${m.userId}`}
                      className={`cf-gestion-equipo-member-item${isSelected ? " is-selected" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        closeAssigneeMenu();
                        persist({ assigneeUserId: String(m.userId) });
                      }}
                    >
                      <div className="cf-gestion-equipo-member">
                        <span className="cf-gestion-equipo-member__name">{displayName}</span>
                        {showEmail && (
                          <span className="cf-gestion-equipo-member__email">{m.email}</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </React.Fragment>
            ))}

            {assignee?.userId && cobranzaAssignedCount > 0 && (
              <button
                type="button"
                className="cf-gestion-equipo-menu__action cf-gestion-equipo-menu__clear"
                onClick={(e) => {
                  e.stopPropagation();
                  closeAssigneeMenu();
                  persist({ assigneeUserId: null });
                }}
              >
                {t("gestionMetaBar.clearAssignee")}
              </button>
            )}
          </div>,
          document.body,
        )
      : null;

  const assigneeBlock = showAssignee ? (
    <div
      className={`cf-gestion-avatar-dropdown${assigneeMenuOpen ? " is-open" : ""}`}
      onClick={stopOnly}
      onMouseDown={blockPointer}
    >
      {assigneeTrigger}
    </div>
  ) : null;

  if (assigneeOnly) {
    if (!showAssignee) return null;

    const assigneeSlot = (
      <div
        className={`cf-gestion-assignee-col-slot${saving ? " cf-gestion-assignee-col-slot--saving" : ""}`}
        onClick={stopOnly}
        onMouseDown={blockPointer}
      >
        {assigneeBlock}
      </div>
    );

    if (disabled) {
      return (
        <>
          {assigneeMenuPortal}
          <Popup
            content="Crear contacto en agenda para asignar responsable"
            position="top right"
            trigger={assigneeSlot}
          />
        </>
      );
    }

    return (
      <>
        {assigneeSlot}
        {assigneeMenuPortal}
      </>
    );
  }

  const metaBar = (
    <div
      className={`cf-gestion-meta-bar${compact ? " cf-gestion-meta-bar--compact" : ""}${
        saving ? " cf-gestion-meta-bar--saving" : ""
      }`}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <ProcessBadge
        label={t("gestionMetaBar.action")}
        value={row?.agenda?.collectionDecision}
        options={decisionOptions}
        disabled={disabled || saving}
        readOnly={processReadOnly}
        onSelect={(collectionDecision) => {
          handleManagementChange({ collectionDecision });
          persist({ collectionDecision: collectionDecision ?? null });
        }}
        onOpen={processReadOnly ? undefined : handleMenuOpen}
        onClose={processReadOnly ? undefined : handleMenuClose}
      />
      {showChannel && (
        <ProcessBadge
          label={t("gestionMetaBar.channel")}
          value={row?.agenda?.collectionChannel}
          options={channelOptions}
          disabled={disabled || saving}
          readOnly={processReadOnly}
          onSelect={(collectionChannel) => {
            handleManagementChange({ collectionChannel });
            persist({ collectionChannel: collectionChannel ?? null });
          }}
          onOpen={processReadOnly ? undefined : handleMenuOpen}
          onClose={processReadOnly ? undefined : handleMenuClose}
        />
      )}
      {assigneeBlock}
    </div>
  );

  if (disabled) {
    return (
      <>
        {assigneeMenuPortal}
        <Popup
          content={t("gestionMetaBar.createContactHint", {
            channelSuffix: showChannel ? t("gestionMetaBar.channelSuffix") : "",
          })}
          position="top right"
          trigger={<span className="cf-gestion-meta-bar-wrap">{metaBar}</span>}
        />
      </>
    );
  }

  return (
    <>
      {metaBar}
      {assigneeMenuPortal}
    </>
  );
}
